import { SelectOption } from '@tassat/ui-definitions';
import { Field } from 'formik';
import * as React from 'react';
import { createFilter } from 'react-select';

import customStyles from './styles';
import { SelectFieldProps } from './types';
import ErrorMessage from '../ErrorMessage';
import muiStyles from '../fieldStyles';
import StyledSelect from '../StyledSelect';

export const defaultOption = {
    label: undefined,
    value: undefined
};

export default function SelectField(props: SelectFieldProps) {
    const {
        id,
        className,
        name,
        label,
        placeholder,
        options,
        disabled,
        menuPosition,
        OptionRenderer,
        SingleSelectValueRenderer,
        nestedValue,
        values,
        errors,
        multiSelect,
        autoComplete,
        hasAllOption = false,
        fieldSize,
        DropdownIndicatorRender,
        updateValuesOnChange
    } = props;

    const classes = muiStyles(props);

    const components = React.useMemo((): {
        Option?: any;
        SingleValue?: any;
        DropdownIndicator?: any;
    } => {
        const renderers = {} as {
            Option?: any;
            SingleValue?: any;
            DropdownIndicator?: any;
        };

        if (OptionRenderer) {
            renderers.Option = OptionRenderer;
        }

        if (SingleSelectValueRenderer) {
            renderers.SingleValue = SingleSelectValueRenderer;
        }

        if (DropdownIndicatorRender) {
            renderers.DropdownIndicator = DropdownIndicatorRender;
        }

        return renderers;
    }, [ DropdownIndicatorRender, OptionRenderer, SingleSelectValueRenderer ]);

    const selectMultipleWithAll = (selectedOptions): SelectOption[] => {
        const all = selectedOptions && selectedOptions.find((option) => option.label.toUpperCase() === 'ALL');
        let result: SelectOption[] = [];

        if (selectedOptions && all) {
            result = [all];
        } else if (selectedOptions && !all) {
            result = [...selectedOptions];
        }

        return result;
    };

    const selectOptionsWithAll = (): SelectOption[] => {
        if (values[name] && values[name].find((opt) => opt.label.toUpperCase() === 'ALL')) {
            return [];
        }

        return options;
    };

    const renderPlaceholder = (): string => {
        if (placeholder) {
            return placeholder;
        }

        if (multiSelect) {
            return 'Select one or more options';
        }

        return 'Select an option';
    };

    const setNestedProperty = (obj, path, value) => {
        const [ head, ...rest ] = path.split('.');

        if (!isNaN(head)) {
            const newObject = [...obj];
            newObject[Number(head)] = (rest.length) ? setNestedProperty(obj[Number(head)], rest.join('.'), value) : value;

            return newObject;
        }

        return {
            ...obj,
            [head]: rest.length ? setNestedProperty(obj[head], rest.join('.'), value) : value
        };
    };

    let value = values;
    if (nestedValue) {
        const valueParts = name.split('.');
        valueParts.forEach((part) => {
            value = value[part];
        });
    } else {
        value = values[name];
    }

    return (
        <Field
            multiple={multiSelect}
            name={name}
            type='text'
        >
            {({ field, form }) => (
                <div className={classes.field}>
                    {label && (
                        <label
                            className={fieldSize === 'large' ? classes.inputLabelLarge : classes.inputLabel}
                            htmlFor={name}
                        >
                            {label}
                        </label>
                    )}
                    <StyledSelect
                        aria-label={name}
                        className={`${className || ''} ${classes.select}`}
                        components={{ ...components }}
                        disabled={disabled}
                        filterOption={autoComplete ? createFilter({ matchFrom: 'any' }) : null}
                        id={id ? `${id}_selectInput` : undefined}
                        isMulti={multiSelect}
                        menuPosition={menuPosition || 'absolute'}
                        name={name}
                        onChange={(selectedOptions) => {
                            form.setFieldTouched(name, true);

                            let otherValues = { ...values };
                            if (updateValuesOnChange) {
                                otherValues = {
                                    ...values,
                                    ...updateValuesOnChange(form, selectedOptions)
                                };
                            }

                            otherValues = setNestedProperty(otherValues, name, hasAllOption ?
                                selectMultipleWithAll(selectedOptions)
                                : selectedOptions);

                            form.setValues(otherValues);
                        }}
                        onMenuClose={() => form.setFieldTouched(name, true)}
                        options={hasAllOption && multiSelect ? selectOptionsWithAll() : options}
                        placeholder={renderPlaceholder()}
                        styles={customStyles}
                        value={value}
                    />
                    <ErrorMessage
                        error={errors[field.name]}
                        field={field}
                        form={form}
                        id={id || ''}
                    />
                </div>
            )}
        </Field>
    );
}